import React, { useState, useEffect } from 'react';
import { Modal } from 'antd';
import { scrollTriggerIntoView } from '../../util/modalUtils';
import { useModalWidth } from '../../hooks/useModalWidth';
import { CheckCircle2 } from 'lucide-react';

// Props: visible, onCancel, onConfirm, onSuccess, isLoading
export default function DeleteAccountModal({ visible, onCancel, onConfirm, onSuccess, isLoading }) {
  const modalWidth = useModalWidth();
  const [step, setStep] = useState(1);
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [otherReason, setOtherReason] = useState('');
  const [confirmText, setConfirmText] = useState('');

  useEffect(() => { if (visible) scrollTriggerIntoView(); }, [visible]);

  // Reset state when modal opens
  useEffect(() => {
    if (visible) {
      setStep(1);
      setSelectedReasons([]);
      setOtherReason('');
      setConfirmText('');
    }
  }, [visible]);

  const reasons = [
    'Found a better alternative',
    'Poor customer service',
    'Didn\'t have the features I wanted',
    'I\'m leaving the country',
    'Other'
  ];

  const toggleReason = (reason) => {
    if (selectedReasons.includes(reason)) {
      setSelectedReasons(selectedReasons.filter(r => r !== reason));
    } else {
      setSelectedReasons([...selectedReasons, reason]);
    }
  };

  const handleConfirmDelete = async () => {
    if (confirmText !== 'DELETE') return;
    try {
      await onConfirm();
      setStep(4);
    } catch (e) {
      // Error is already toasted by the hook; stay on step 3
    }
  };

  const handleCloseSuccess = () => {
    onSuccess();
    onCancel();
  };

  const renderStep1 = () => (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 16 }}>Delete Account</div>
      <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 8 }}>Head Up! ⚠️</div>
      <p style={{ color: '#555', marginBottom: 16 }}>Deleting your account will mean the following</p>
      <ul style={{ textAlign: 'left', maxWidth: 520, margin: '0 auto 24px', color: '#444', paddingLeft: 18, lineHeight: '1.6' }}>
        <li style={{ marginBottom: 8 }}>Your will be temporary deleted account but will be permanently deleted after 30 days</li>
        <li style={{ marginBottom: 8 }}>You lose access to your login on the self service portal and Ghana MOM App</li>
        <li style={{ marginBottom: 8 }}>Your policies can still be retrieved</li>
      </ul>
      <button
        className="btn btn-primary"
        onClick={() => setStep(2)}
        style={{ background: '#0D7C4B', borderColor: '#0D7C4B', color: '#fff', padding: '12px 24px', borderRadius: 9999, width: '100%', maxWidth: 320, border: 'none', cursor: 'pointer' }}
      >
        I understand the risks, continue
      </button>
    </div>
  );

  const renderStep2 = () => (
    <div>
      <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 8, textAlign: 'center' }}>Delete Account</div>
      <div style={{ textAlign: 'center', marginBottom: 8, color: '#333' }}>We're sorry to see you go 🥺</div>
      <div style={{ textAlign: 'center', marginBottom: 16, color: '#555' }}>Why are you leaving us</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
        {reasons.map(reason => (
          <div
            key={reason}
            onClick={() => toggleReason(reason)}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 16px',
              border: '1px solid #e0e0e0',
              borderRadius: 8,
              cursor: 'pointer',
              background: selectedReasons.includes(reason) ? '#f0f9f4' : '#fff',
              borderColor: selectedReasons.includes(reason) ? '#0D7C4B' : '#e0e0e0'
            }}
          >
            <span style={{ fontSize: 14, color: '#333' }}>{reason}</span>
            <div style={{
              width: 20,
              height: 20,
              border: '2px solid #ccc',
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: selectedReasons.includes(reason) ? '#0D7C4B' : 'transparent',
              borderColor: selectedReasons.includes(reason) ? '#0D7C4B' : '#ccc'
            }}>
              {selectedReasons.includes(reason) && <span style={{ color: '#fff', fontSize: 12 }}>✓</span>}
            </div>
          </div>
        ))}
      </div>
      {selectedReasons.includes('Other') && (
        <textarea
          placeholder="enter reason"
          value={otherReason}
          onChange={e => setOtherReason(e.target.value)}
          style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #e0e0e0', minHeight: 80, marginBottom: 16, fontFamily: 'inherit', fontSize: 14 }}
        />
      )}
      <button
        className="btn btn-primary"
        onClick={() => setStep(3)}
        style={{ background: '#0D7C4B', borderColor: '#0D7C4B', color: '#fff', padding: '12px 24px', borderRadius: 9999, width: '100%', border: 'none', cursor: 'pointer' }}
      >
        Proceed
      </button>
    </div>
  );

  const renderStep3 = () => (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 16 }}>Confirm Delete</div>
      <p style={{ color: '#555', marginBottom: 24 }}>Type "DELETE" to confirm delete of your account</p>
      <input
        type="text"
        value={confirmText}
        onChange={e => setConfirmText(e.target.value)}
        placeholder="DELETE"
        style={{ width: '100%', padding: '12px 16px', borderRadius: 8, border: '1px solid #e0e0e0', fontSize: 16, marginBottom: 24, textAlign: 'center', outline: 'none' }}
      />
      <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
        <button
          className="btn btn-primary"
          onClick={handleConfirmDelete}
          disabled={isLoading || confirmText !== 'DELETE'}
          style={{
            background: isLoading || confirmText !== 'DELETE' ? '#ccc' : '#0D7C4B',
            borderColor: isLoading || confirmText !== 'DELETE' ? '#ccc' : '#0D7C4B',
            color: '#fff',
            padding: '12px 24px',
            borderRadius: 9999,
            flex: 1,
            border: 'none',
            cursor: isLoading || confirmText !== 'DELETE' ? 'not-allowed' : 'pointer'
          }}
        >
          {isLoading ? 'Processing...' : 'Confirm Delete'}
        </button>
        <button
          className="btn btn-white"
          onClick={onCancel}
          style={{ padding: '12px 24px', borderRadius: 9999, border: '1px solid #0D7C4B', color: '#0D7C4B', flex: 1, background: '#fff', cursor: 'pointer' }}
        >
          No, Cancel
        </button>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div style={{ textAlign: 'center', padding: '24px 0' }}>
      <div style={{
        width: 80,
        height: 80,
        background: '#4CAF50',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 24px'
      }}>
        <CheckCircle2 size={40} color="#fff" />
      </div>
      <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 8 }}>Success</div>
      <p style={{ color: '#555', marginBottom: 24, lineHeight: 1.5 }}>
        Your account has been temporary deleted.<br />
        You cant reactivate the account within 30<br />
        days after which it is permanently deleted
      </p>
      <button
        className="btn btn-primary"
        onClick={handleCloseSuccess}
        style={{ background: '#0D7C4B', borderColor: '#0D7C4B', color: '#fff', padding: '12px 24px', borderRadius: 9999, width: '100%', maxWidth: 320, border: 'none', cursor: 'pointer' }}
      >
        Close
      </button>
    </div>
  );

  const renderContent = () => {
    if (step === 4) return renderStep4();
    if (step === 3) return renderStep3();
    if (step === 2) return renderStep2();
    return renderStep1();
  };

  return (
    <Modal
      centered
      open={visible}
      destroyOnClose={true}
      closable={step !== 4}
      footer={null}
      onCancel={onCancel}
      width={modalWidth}
      zIndex={4000}
    >
      <div className="p-4">
        {renderContent()}
      </div>
    </Modal>
  );
}
